import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Check } from 'lucide-react';
import { getToken } from '@/lib/auth';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

export const ImagePickerDialog = ({ open, onOpenChange, onSelect }) => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get('/admin/media?limit=50').then(res => {
        setMedia(res.data.items || []);
      }).catch(console.error);
      setSelected(null);
      setExternalUrl('');
    }
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5MB limit.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken();
      const res = await fetch(`${API_BASE}/admin/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data._id) {
        const imageUrl = `/api/media/${data._id}`;
        onSelect(imageUrl, data.filename);
        onOpenChange(false);
        toast.success('Image uploaded and inserted.');
      }
    } catch (err) {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelectExisting = (item) => {
    setSelected(item._id);
    const imageUrl = `/api/media/${item._id}`;
    onSelect(imageUrl, item.alt_text || item.filename);
    onOpenChange(false);
  };

  const handleExternalUrl = () => {
    if (!externalUrl.trim()) return;
    onSelect(externalUrl.trim(), 'image');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
            Insert Image
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Upload new */}
          <div>
            <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--gold))] mb-3">
              Upload New Image
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              id="essay-image-upload"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="w-full border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] hover:bg-[rgba(27,77,68,0.12)] font-['Inter'] text-sm py-5"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Choose File & Insert'}
            </Button>
          </div>

          {/* External URL */}
          <div>
            <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-3">
              Or Paste Image URL
            </p>
            <div className="flex gap-2">
              <Input
                value={externalUrl}
                onChange={e => setExternalUrl(e.target.value)}
                placeholder="https://..."
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
              <Button
                onClick={handleExternalUrl}
                disabled={!externalUrl.trim()}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm shrink-0"
              >
                Insert
              </Button>
            </div>
          </div>

          {/* Existing media library */}
          {media.length > 0 && (
            <div>
              <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-3">
                Or Choose from Media Library
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                {media.map(item => (
                  <button
                    key={item._id}
                    onClick={() => handleSelectExisting(item)}
                    className={`relative aspect-square rounded-lg overflow-hidden border transition-all duration-200 ${
                      selected === item._id
                        ? 'border-[hsl(var(--gold))] ring-2 ring-[hsl(var(--gold))]'
                        : 'border-[rgba(168,181,178,0.12)] hover:border-[rgba(201,168,76,0.3)]'
                    }`}
                  >
                    <img
                      src={`/api/media/${item._id}`}
                      alt={item.alt_text || item.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {selected === item._id && (
                      <div className="absolute inset-0 bg-[rgba(0,0,0,0.4)] flex items-center justify-center">
                        <Check className="h-6 w-6 text-[hsl(var(--gold))]" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
